import { useFormik } from "formik";
import { useCallback, useEffect, useState } from "react";
import { AiOutlineDelete } from "react-icons/ai";
import { LuPencilLine } from "react-icons/lu";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import * as Yup from "yup";
import ConfirmationPage from "../../common/ConfirmationPage";
import Table from "../../common/Table";
import { fileUpload } from "../../redux/services/fileUploadSlice";
import { addYoutubeVisuals, deleteYoutubeVisuals, editYoutubeVisuals, getAllYoutubeVisuals, getSingleYoutubeVisuals } from "../../redux/services/youtubeVisualsSlice";
import YoutubeVisualsForm from "./youtubeVisualsForm";

const YoutubeVisuals = () => {
  const dispatch = useDispatch();
  const getYoutubeVisualsData = useSelector((state) => state?.youtubeVisuals?.youtubeVisualsData);
  const getSingleYoutubeVisualsData = useSelector((state) => state?.youtubeVisuals?.singleYoutubeVisualsData);
  const [modelOpen, setModelOpen] = useState(false);
  const [editId, setEditId] = useState();
  const [itemToDelete, setItemToDelete] = useState(null);
  const [confirm, setConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [page, setPage] = useState(0);
  const limitOptions = [
    { value: 10, label: 10 },
    { value: 30, label: 30 },
    { value: 50, label: 50 },
    { value: 100, label: 100 },
  ];
  const [pageLimit, setPageLimit] = useState(limitOptions[0]?.value);

  const getAllYoutubeVisualsApi = useCallback(async () => {
    try {
      await dispatch(getAllYoutubeVisuals({ page: page + 1, limit: pageLimit }));
    } catch (error) {}
  }, [dispatch, page, pageLimit]);

  useEffect(() => {
    getAllYoutubeVisualsApi();
  }, [getAllYoutubeVisualsApi]);

  const initialValues = {
    thumbnail: undefined,
    link: "",
    title: "",
  };
  const validationSchema = Yup.object({
    thumbnail: Yup.mixed().required("Thumbnail is required"),
    link: Yup.string().required("Link is required"),
    title: Yup.string().required("Title is required"),
  });
  const onSubmit = async (values) => {
    setLoading(true);
    const payload = { ...values };
    if (editId) {
      payload.youtubeVisualsId = editId;
    }
    try {
      let response;
      if (editId) {
        response = await dispatch(editYoutubeVisuals(payload));
      } else {
        response = await dispatch(addYoutubeVisuals(payload));
      }
      if (response?.payload?.status === 200) {
        toast.success(response?.payload?.message, {
          autoClose: 2000,
          pauseOnHover: false,
        });
        setLoading(false);
        setModelOpen(false);
        resetForm();
        setEditId();
        setPreview();
        getAllYoutubeVisualsApi();
      } else {
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };
  const handleFileUpload = async (file) => {
    const formData = new FormData();
    formData.append("image", file);
    try {
      const response = await dispatch(fileUpload(formData));
      if (response) {
        setFieldValue("thumbnail", response?.payload);
      }
    } catch (error) {
      alert("Error uploading file. Please try again.");
    }
  };
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const imageURL = URL.createObjectURL(file);
      setPreview(imageURL);
      setFieldValue("thumbnail", imageURL);
      handleFileUpload(file);
    }
  };
  const deleteYoutubeVisualsData = async () => {
    try {
      const response = await dispatch(deleteYoutubeVisuals(itemToDelete));
      toast.success(response?.payload?.message, {
        autoClose: 2000,
        pauseOnHover: false,
      });
      setConfirm(false);
      getAllYoutubeVisualsApi();
    } catch (error) {}
  };
  const { values, errors, touched, handleChange, handleBlur, handleSubmit, setValues, setFieldValue, resetForm } = useFormik({
    initialValues,
    validationSchema,
    onSubmit,
  });

  const getSingleYoutubeVisualsApi = useCallback(async () => {
    try {
      await dispatch(getSingleYoutubeVisuals(editId));
    } catch (error) {}
  }, [dispatch, editId]);

  useEffect(() => {
    if (editId) {
      getSingleYoutubeVisualsApi();
    }
  }, [editId, getSingleYoutubeVisualsApi]);

  useEffect(() => {
    if (getSingleYoutubeVisualsData) {
      setValues({
        thumbnail: getSingleYoutubeVisualsData?.thumbnail,
        title: getSingleYoutubeVisualsData?.title,
        link: getSingleYoutubeVisualsData?.link,
      });
    }
    setPreview(getSingleYoutubeVisualsData?.thumbnail);
  }, [getSingleYoutubeVisualsData, setValues]);
  const columns = [
    {
      header: "S/N",
      key: "index",
      render: (_, __, index) => page * pageLimit + index + 1,
    },
    {
      header: "Thumbnail",
      key: "thumbnail",
      render: (value) =>
        value ? (
          <div className="flex justify-center">
            <img src={value} alt="Youtube Visuals" className="h-20 w-28" />
          </div>
        ) : (
          "—"
        ),
    },
    {
      header: "Link",
      key: "link",
      render: (value) =>
        value ? (
          <Link to={value} target="_blank" rel="noopener noreferrer" className={`p-4 border-btext-center ${value && "text-primary underline"}`}>
            {value}
          </Link>
        ) : (
          "—"
        ),
    },
    { header: "Title", key: "title" },
  ];

  const actions = [
    {
      icon: <LuPencilLine className="w-5 h-5" />,
      onClick: (row) => {
        setModelOpen(true);
        setEditId(row._id);
      },
    },
    {
      icon: <AiOutlineDelete className="w-6 h-6" />,
      onClick: (row) => {
        setItemToDelete(row._id);
        setConfirm(true);
      },
    },
  ];
  return (
    <div>
      <Table title="Youtube Visuals" buttonTitle="Add New Youtube Visuals" onAddClick={() => setModelOpen(true)} columns={columns} data={getYoutubeVisualsData?.youtube_visuals_data} actions={actions} pageCount={getYoutubeVisualsData?.state?.page_limit} onPageChange={(selectedItem) => setPage(selectedItem.selected)} pageLimitOptions={limitOptions} selectedPageLimit={pageLimit} onPageLimitChange={setPageLimit} />
      {confirm && <ConfirmationPage topicName="Youtube Visuals" confirm={confirm} onDelete={deleteYoutubeVisualsData} onCancel={() => setConfirm(false)} />}
      {modelOpen && (
        <YoutubeVisualsForm
          modelOpen={modelOpen}
          setModelOpen={setModelOpen}
          onClose={() => {
            setModelOpen(false);
            resetForm();
            setPreview();
            setEditId();
          }}
          values={values}
          handleChange={handleChange}
          editId={editId}
          handleBlur={handleBlur}
          errors={errors}
          touched={touched}
          handleSubmit={handleSubmit}
          loading={loading}
          handleImageChange={handleImageChange}
          preview={preview}
        />
      )}
    </div>
  );
};

export default YoutubeVisuals;
